export type RemoteFastEntry = {
   historyId?: number | null
   startTime: number
   endTime?: number | null
   duration: number
   durationUpdatedAt: number
   notifiedCompletion: boolean
   displayMode: 'elapsed' | 'remaining'
}

/**
 * Builds a `start-end` dedupe key for a fast entry, or null when its times are invalid.
 *
 * @param entry - The fast entry.
 * @returns The key, or null.
 */
function buildHistoryKey(entry: RemoteFastEntry) {
   if (!entry || typeof entry !== 'object') {
      return null
   }
   const start = typeof entry.startTime === 'number' ? entry.startTime : null
   const end = typeof entry.endTime === 'number' ? entry.endTime : null
   if (!Number.isFinite(start) || !Number.isFinite(end)) {
      return null
   }
   return `${start}-${end}`
}

/**
 * Merges remote fast-history entries over local ones, dropping local entries
 * that share a server id or a start/end window with a remote entry.
 *
 * @param localEntries - The local history.
 * @param remoteEntries - The deserialized remote history.
 * @returns Remote entries followed by the retained local ones (unsorted).
 */
export function mergeRemoteHistoryEntries(
   localEntries: RemoteFastEntry[],
   remoteEntries: RemoteFastEntry[],
) {
   const seenIds = new Set<number>()
   const seenKeys = new Set<string>()
   for (const entry of remoteEntries) {
      if (entry.historyId) {
         seenIds.add(entry.historyId)
      }
      const key = buildHistoryKey(entry)
      if (key) {
         seenKeys.add(key)
      }
   }
   const retainedLocal = localEntries.filter(entry => {
      if (!entry || typeof entry !== 'object') {
         return false
      }
      if (entry.historyId && seenIds.has(entry.historyId)) {
         return false
      }
      const key = buildHistoryKey(entry)
      return !(key && seenKeys.has(key))
   })
   return [...remoteEntries, ...retainedLocal]
}

/**
 * Returns whether the local in-progress fast should win over the remote one:
 * same fast (same start), but the local duration was adjusted more recently.
 *
 * @param local - The local current fast, or null.
 * @param remote - The remote current fast, or null.
 * @returns True when the local fast should be kept.
 */
export function shouldKeepLocalCurrentFast(
   local: RemoteFastEntry | null,
   remote: RemoteFastEntry | null,
) {
   if (!local || !remote) {
      return false
   }
   return (
      typeof local.startTime === 'number' &&
      local.startTime === remote.startTime &&
      Number.isFinite(local.durationUpdatedAt) &&
      Number.isFinite(remote.durationUpdatedAt) &&
      local.durationUpdatedAt > remote.durationUpdatedAt
   )
}
